import { IPurchasableVariant } from "@smartrr/shared/entities/PurchasableVariant";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import {
  IPurchaseStateLineItem,
  IPurchaseStateLineItemPricingPolicy,
} from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import remove from 'lodash/remove';
import { toast } from "react-toastify";

import { IUpcomingCartLineItem, smartrrAccountConfig } from "@customer-app/account/config";

import { getItemPrice, getOrderCount } from "../utils/Pricing/Item";

export interface ICartChanges {
  addedItems?: IUpcomingCartLineItem[];
  updatedItems?: IUpcomingCartLineItem[];
  removedItemIds?: string[];
}

/**
 * Applies custom (store-specific) validation of modifications in cart of upcoming orders.
 * @param originalCps - original state of subscription.
 * @param changes - changes in the cart that customer tries to perform.
 * @returns true if modifications are valid; otherwise - false.
 */
export function validateCartChanges(delivery: PurchaseStateAndDelivery, changes: ICartChanges): boolean {
  const validationCallback = smartrrAccountConfig.upcomingCartValidationCallback;
  if (!validationCallback) {
    return true;
  }

  const changedCartItems = buildChangedCartItems(delivery, changes);
  const validationResult = validationCallback(changedCartItems);

  if (!validationResult.isValid) {
    const message = validationResult.errorMessage || "Invalid modification of the order";
    toast.error(message);
    return false;
  }

  return true;
}

/**
 * Converts subscription line item to "validatable" cart item.
 */
export function toCartItem(
  delivery: PurchaseStateAndDelivery,
  lineItem: IPurchaseStateLineItem,
  chosenVariant?: IPurchasableVariant
): IUpcomingCartLineItem {
  const policy = lineItem.pricingPolicy as IPurchaseStateLineItemPricingPolicy;
  return {
    id: lineItem.id,
    quantity: lineItem.quantity,
    isAddOn: lineItem.isAddOn,
    variantShopifyId: lineItem.vnt.shopifyId,
    sellingPlanShopifyId: lineItem.sellingPlanId,
    estimatedPrice: getItemPrice(chosenVariant ? chosenVariant : lineItem.vnt, delivery.purchaseState.currency, {
      policy,
      sellingPlan: null,
      orderNumber: getOrderCount(delivery),
    }),
  };
}

function buildChangedCartItems(
  delivery: PurchaseStateAndDelivery,
  changes: ICartChanges
): IUpcomingCartLineItem[] {
  const { removedItemIds = [], updatedItems = [], addedItems = [] } = changes;

  const resultingItems: IUpcomingCartLineItem[] = delivery.purchaseState.stLineItems.reduce((acc, line) => {
    const scheduleIndex = Number.isInteger(delivery.indexFromScheduleStart) ? delivery.indexFromScheduleStart : 0;
    const sameIndex = Number.isInteger(line.skdIdx) ? line.skdIdx === scheduleIndex : true;
    if (sameIndex) {
      acc.push(toCartItem(delivery, line));
    }
    return acc;
  }, [] as IUpcomingCartLineItem[]);

  for (const removedId of removedItemIds) {
    remove(resultingItems, item => item.id === removedId);
  }

  for (const updatedItem of updatedItems) {
    const itemIndex = resultingItems.findIndex(item => item.id === updatedItem.id);
    if (itemIndex !== -1) {
      resultingItems[itemIndex] = updatedItem;
    }
  }

  for (const addedItem of addedItems) {
    resultingItems.push(addedItem);
  }

  return resultingItems;
}
